import React, { useRef, useState, useEffect, useCallback } from "react";
import {
	Camera as CameraIcon,
	X,
	RotateCw,
	Circle,
	CheckCircle,
	Video,
	Mic,
	Upload,
	Undo2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import Waveform from "@/components/Waveform";

/**
 * Custom Camera Component
 * Provides full control over camera UI and functionality
 *
 * @param {Object} props
 * @param {Function} props.onCapture - Callback when photo is captured (receives File object)
 * @param {Function} props.onClose - Callback when camera is closed
 * @param {string} props.mode - 'photo' or 'video' (default: 'photo')
 * @param {boolean} props.showPreview - Show preview after capture (default: true)
 */
const Camera = ({
	onCapture,
	onClose,
	mode: initialMode = "photo",
	showPreview = true,
}) => {
	const videoRef = useRef(null);
	const previewVideoRef = useRef(null);
	const canvasRef = useRef(null);
	const mediaRecorderRef = useRef(null);
	const recordedChunksRef = useRef([]);
	const audioRecorderRef = useRef(null);
	const audioContextRef = useRef(null);
	const analyserRef = useRef(null);
	const animationFrameRef = useRef(null);
	const fileInputRef = useRef(null);
	const videoInputRef = useRef(null);
	const audioInputRef = useRef(null);

	const [stream, setStream] = useState(null);
	const [capturedImage, setCapturedImage] = useState(null);
	const [capturedVideo, setCapturedVideo] = useState(null);
	const [isRecording, setIsRecording] = useState(false);
	const [recordingDuration, setRecordingDuration] = useState(0);
	const [facingMode, setFacingMode] = useState("user"); // 'user' for front, 'environment' for back
	const [error, setError] = useState(null);
	const [hasPermission, setHasPermission] = useState(false);
	const [mode, setMode] = useState(initialMode); // Allow mode switching
	const [showCrop, setShowCrop] = useState(false);
	const [imageToCrop, setImageToCrop] = useState(null);
	const [crop, setCrop] = useState(null);
	const [completedCrop, setCompletedCrop] = useState(null);
	const imgRef = useRef(null);
	const [isAudioRecording, setIsAudioRecording] = useState(false);
	const [audioLevels, setAudioLevels] = useState([]);
	const [capturedAudio, setCapturedAudio] = useState(null);

	// Timer for recording duration
	useEffect(() => {
		let interval;
		if (isRecording || isAudioRecording) {
			interval = setInterval(() => {
				setRecordingDuration((prev) => prev + 1);
			}, 1000);
		} else {
			setRecordingDuration(0);
		}
		return () => clearInterval(interval);
	}, [isRecording, isAudioRecording]);

	// Initialize camera
	useEffect(() => {
		startCamera();
		return () => {
			stopCamera();
		};
	}, [facingMode]);

	const startCamera = async () => {
		try {
			setError(null);

			// Stop existing stream if any
			if (stream) {
				stream.getTracks().forEach((track) => track.stop());
			}

			const constraints = {
				video: {
					facingMode: facingMode,
					width: { ideal: 1920 },
					height: { ideal: 1080 },
				},
				audio: mode === "video",
			};

			const mediaStream =
				await navigator.mediaDevices.getUserMedia(constraints);
			setStream(mediaStream);
			setHasPermission(true);

			if (videoRef.current) {
				videoRef.current.srcObject = mediaStream;
			}
		} catch (err) {
			console.error("Error accessing camera:", err);
			setError("Unable to access camera. Please check permissions.");
			setHasPermission(false);
		}
	};

	const stopCamera = () => {
		if (stream) {
			stream.getTracks().forEach((track) => track.stop());
			setStream(null);
		}
	};

	const switchCamera = () => {
		setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
	};

	const capturePhoto = () => {
		if (!videoRef.current || !canvasRef.current) {return;}

		const video = videoRef.current;
		const canvas = canvasRef.current;

		// Always crop to 9:16 (mobile aspect ratio) first - never show full screen version
		const targetWidth = 480;
		const targetHeight = 854; // 480 * 16/9
		canvas.width = targetWidth;
		canvas.height = targetHeight;

		const ctx = canvas.getContext("2d");

		// Calculate crop dimensions to get center crop at 9:16
		const videoAspect = video.videoWidth / video.videoHeight;
		const targetAspect = 9 / 16;

		let sourceWidth, sourceHeight, sourceX, sourceY;

		if (videoAspect > targetAspect) {
			// Video is wider - crop sides
			sourceHeight = video.videoHeight;
			sourceWidth = sourceHeight * targetAspect;
			sourceX = (video.videoWidth - sourceWidth) / 2;
			sourceY = 0;
		} else {
			// Video is taller - crop top/bottom
			sourceWidth = video.videoWidth;
			sourceHeight = sourceWidth / targetAspect;
			sourceX = 0;
			sourceY = (video.videoHeight - sourceHeight) / 2;
		}

		// Draw cropped video frame to canvas
		// Flip horizontally for front-facing camera (mirror view)
		if (facingMode === "user") {
			ctx.save();
			ctx.translate(targetWidth, 0);
			ctx.scale(-1, 1);
		}

		ctx.drawImage(
			video,
			sourceX,
			sourceY,
			sourceWidth,
			sourceHeight,
			0,
			0,
			targetWidth,
			targetHeight
		);

		// Reset transform for front-facing camera
		if (facingMode === "user") {
			ctx.restore();
		}

		// Convert canvas to blob and show crop interface
		// User will then crop this 9:16 version to a square
		canvas.toBlob(
			(blob) => {
				const imageUrl = URL.createObjectURL(blob);
				setImageToCrop(imageUrl);
				setShowCrop(true);
				// Don't set crop here - let onImageLoaded handle it based on actual displayed dimensions
				// This ensures proper centering based on how the image is actually displayed
			},
			"image/jpeg",
			0.95
		);
	};

	const onCropChange = (newCrop) => {
		setCrop(newCrop);
	};

	const onCropComplete = (newCrop) => {
		setCrop(newCrop);
		setCompletedCrop(newCrop);
	};

	const onImageLoaded = useCallback(
		(e) => {
			const img = e.currentTarget;
			if (img && !crop) {
				// Function to calculate and set crop based on actual displayed dimensions
				const calculateCrop = () => {
					// Get the actual displayed dimensions of the image
					// ReactCrop uses the displayed size, not natural size
					const displayedWidth =
						img.offsetWidth || img.clientWidth || img.width || 0;
					const displayedHeight =
						img.offsetHeight || img.clientHeight || img.height || 0;

					// If dimensions aren't available yet, wait a bit more
					if (displayedWidth === 0 || displayedHeight === 0) {
						setTimeout(calculateCrop, 50);
						return;
					}

					// Crop size should NEVER exceed the width of the image
					// Since it's a square crop, use the smaller of width or height
					// This ensures the crop fits completely within the image
					const cropSize = Math.min(displayedWidth, displayedHeight);

					// Center the crop on the image
					const initialX = Math.max(0, (displayedWidth - cropSize) / 2);
					const initialY = Math.max(0, (displayedHeight - cropSize) / 2);

					setCrop({
						unit: "px",
						x: initialX,
						y: initialY,
						width: cropSize,
						height: cropSize,
					});
				};

				// Wait for image to be fully rendered before calculating crop
				// Use requestAnimationFrame to ensure layout is complete
				requestAnimationFrame(() => {
					setTimeout(calculateCrop, 50);
				});
			}
		},
		[crop]
	);

	const applyCrop = async () => {
		if (!imageToCrop || !crop || !imgRef.current) {return;}

		try {
			const croppedImageUrl = await getCroppedImg(imgRef.current, crop);
			setCapturedImage(croppedImageUrl);
			setShowCrop(false);

			// Clear crop state after applying
			setCrop(null);

			if (!showPreview) {
				const response = await fetch(croppedImageUrl);
				const blob = await response.blob();
				const file = new File([blob], `photo-${Date.now()}.jpg`, {
					type: "image/jpeg",
				});
				onCapture(file);
				handleClose();
			}
		} catch (error) {
			console.error("Error applying crop:", error);
			setError("Failed to apply crop. Please try again.");
		}
	};

	const handleUndo = () => {
		// Revoke the current captured image URL to free memory
		if (capturedImage) {
			URL.revokeObjectURL(capturedImage);
		}
		// Clear captured image and show crop screen again
		setCapturedImage(null);
		setShowCrop(true);
		// Reset crop to null so it will be recalculated on image load
		setCrop(null);
	};

	const getCroppedImg = (image, crop) => {
		if (!crop || !image) {
			return Promise.reject(new Error("Invalid crop or image"));
		}

		return new Promise((resolve, reject) => {
			const canvas = document.createElement("canvas");
			const ctx = canvas.getContext("2d");
			const scaleX = image.naturalWidth / image.width;
			const scaleY = image.naturalHeight / image.height;

			// Square crop should be 480x480 (same width as phone crop)
			const targetSize = 480;
			canvas.width = targetSize;
			canvas.height = targetSize;

			const pixelCrop = {
				x: crop.x * scaleX,
				y: crop.y * scaleY,
				width: crop.width * scaleX,
				height: crop.height * scaleY,
			};

			ctx.drawImage(
				image,
				pixelCrop.x,
				pixelCrop.y,
				pixelCrop.width,
				pixelCrop.height,
				0,
				0,
				targetSize,
				targetSize
			);

			canvas.toBlob(
				(blob) => {
					if (!blob) {
						reject(new Error("Canvas is empty"));
						return;
					}
					const fileUrl = URL.createObjectURL(blob);
					resolve(fileUrl);
				},
				"image/jpeg",
				0.95
			);
		});
	};

	const startRecording = () => {
		if (!stream || !videoRef.current) {return;}

		recordedChunksRef.current = [];

		try {
			// Create a canvas to crop the video to phone size (9:16 aspect ratio)
			const video = videoRef.current;
			const canvas = document.createElement("canvas");

			// Set canvas to phone dimensions (480x854 for 9:16 ratio)
			const targetWidth = 480;
			const targetHeight = 854; // 480 * 16/9
			canvas.width = targetWidth;
			canvas.height = targetHeight;

			const ctx = canvas.getContext("2d");

			// Calculate crop dimensions to get center crop at 9:16
			const videoAspect = video.videoWidth / video.videoHeight;
			const targetAspect = 9 / 16;

			let sourceWidth, sourceHeight, sourceX, sourceY;

			if (videoAspect > targetAspect) {
				// Video is wider - crop sides
				sourceHeight = video.videoHeight;
				sourceWidth = sourceHeight * targetAspect;
				sourceX = (video.videoWidth - sourceWidth) / 2;
				sourceY = 0;
			} else {
				// Video is taller - crop top/bottom
				sourceWidth = video.videoWidth;
				sourceHeight = sourceWidth / targetAspect;
				sourceX = 0;
				sourceY = (video.videoHeight - sourceHeight) / 2;
			}

			// Capture frames and draw to canvas
			const fps = 30;
			const captureInterval = setInterval(() => {
				// Flip horizontally for front-facing camera (mirror view)
				if (facingMode === "user") {
					ctx.save();
					ctx.translate(targetWidth, 0);
					ctx.scale(-1, 1);
				}

				ctx.drawImage(
					video,
					sourceX,
					sourceY,
					sourceWidth,
					sourceHeight,
					0,
					0,
					targetWidth,
					targetHeight
				);

				// Reset transform for front-facing camera
				if (facingMode === "user") {
					ctx.restore();
				}
			}, 1000 / fps);

			// Get stream from canvas
			const canvasStream = canvas.captureStream(fps);

			// Add audio from original stream if available
			const audioTracks = stream.getAudioTracks();
			if (audioTracks.length > 0) {
				canvasStream.addTrack(audioTracks[0]);
			}

			// Try different codecs for better compatibility
			// Prefer VP8 as it's more widely supported and plays smoother
			let options = {
				mimeType: "video/webm;codecs=vp8",
				videoBitsPerSecond: 2500000, // 2.5 Mbps for good quality/size balance
			};
			if (!MediaRecorder.isTypeSupported(options.mimeType)) {
				options = {
					mimeType: "video/webm;codecs=vp9",
					videoBitsPerSecond: 2000000, // Slightly lower for VP9
				};
			}
			if (!MediaRecorder.isTypeSupported(options.mimeType)) {
				options = {
					mimeType: "video/webm",
					videoBitsPerSecond: 2500000,
				};
			}

			const mediaRecorder = new MediaRecorder(canvasStream, options);

			// Store canvas and interval for cleanup
			mediaRecorder.captureInterval = captureInterval;
			mediaRecorder.canvas = canvas;

			mediaRecorder.ondataavailable = (event) => {
				console.log("Data available:", event.data.size);
				if (event.data && event.data.size > 0) {
					recordedChunksRef.current.push(event.data);
				}
			};

			mediaRecorder.onstop = () => {
				console.log(
					"Recording stopped, chunks:",
					recordedChunksRef.current.length
				);

				// Wait longer to ensure all chunks are finalized (especially important for desktop)
				setTimeout(() => {
					if (recordedChunksRef.current.length === 0) {
						console.error("No video data recorded");
						setError("No video data was recorded. Please try again.");
						return;
					}

					// Use the mimeType from the recorder for the blob
					const mimeType = mediaRecorder.mimeType || "video/webm";
					const blob = new Blob(recordedChunksRef.current, {
						type: mimeType,
					});
					console.log("Blob created:", blob.size, "bytes", "type:", mimeType);

					// Verify blob is valid
					if (blob.size === 0) {
						console.error("Blob is empty");
						setError("Video recording is empty. Please try again.");
						return;
					}

					// Stop camera stream before creating video URL (frees resources)
					if (stream) {
						stream.getTracks().forEach((track) => track.stop());
						setStream(null);
						console.log("Camera stream stopped");
					}

					// Clean up canvas and interval
					if (mediaRecorder.captureInterval) {
						clearInterval(mediaRecorder.captureInterval);
					}

					// Create blob URL and wait a bit for it to be ready (helps with desktop buffering)
					const videoUrl = URL.createObjectURL(blob);
					console.log("Video URL created:", videoUrl);

					// Give the blob URL a moment to be fully ready before setting state
					// This helps prevent buffering issues on desktop
					setTimeout(() => {
						setCapturedVideo(videoUrl);
						console.log("capturedVideo state updated (after delay)");
					}, 200); // Additional delay for blob URL to be ready

					if (!showPreview) {
						const file = new File([blob], `video-${Date.now()}.webm`, {
							type: mimeType,
						});
						onCapture(file);
						handleClose();
					}
				}, 300); // Longer delay to ensure all chunks are fully collected and finalized
			};

			// Request data every 100ms to ensure we capture data
			mediaRecorder.start(100);
			mediaRecorderRef.current = mediaRecorder;
			setIsRecording(true);
			console.log("Recording started with mimeType:", options.mimeType);
		} catch (err) {
			console.error("Error starting recording:", err);
			setError("Unable to start recording");
		}
	};

	const stopRecording = () => {
		if (mediaRecorderRef.current && isRecording) {
			// Request final data before stopping
			if (mediaRecorderRef.current.state === "recording") {
				mediaRecorderRef.current.requestData();
			}

			// Clean up canvas capture interval
			if (mediaRecorderRef.current.captureInterval) {
				clearInterval(mediaRecorderRef.current.captureInterval);
			}

			mediaRecorderRef.current.stop();
			setIsRecording(false);
			console.log("Stop recording called");
		}
	};

	const handleConfirm = () => {
		if (capturedImage) {
			// Convert captured image to file
			fetch(capturedImage)
				.then((res) => res.blob())
				.then((blob) => {
					const file = new File([blob], `photo-${Date.now()}.jpg`, {
						type: "image/jpeg",
					});
					onCapture(file);
					handleClose();
				});
		} else if (capturedVideo) {
			// Convert captured video to file
			fetch(capturedVideo)
				.then((res) => res.blob())
				.then((blob) => {
					const file = new File([blob], `video-${Date.now()}.webm`, {
						type: "video/webm",
					});
					onCapture(file);
					handleClose();
				});
		} else if (capturedAudio) {
			// Convert captured audio to file
			fetch(capturedAudio)
				.then((res) => res.blob())
				.then((blob) => {
					const file = new File([blob], `audio-${Date.now()}.webm`, {
						type: "audio/webm",
					});
					onCapture(file);
					handleClose();
				});
		}
	};

	const startAudioRecording = async () => {
		try {
			setError(null);

			// Stop camera if running
			if (stream) {
				stream.getTracks().forEach((track) => track.stop());
				setStream(null);
			}

			// Get user media stream for audio only
			const audioStream = await navigator.mediaDevices.getUserMedia({
				audio: true,
			});
			audioRecorderRef.current = audioStream;

			// Set up Web Audio API for visualization
			audioContextRef.current = new (window.AudioContext ||
				window.webkitAudioContext)();
			analyserRef.current = audioContextRef.current.createAnalyser();
			const source =
				audioContextRef.current.createMediaStreamSource(audioStream);
			source.connect(analyserRef.current);
			analyserRef.current.fftSize = 256;

			// Create media recorder
			mediaRecorderRef.current = new MediaRecorder(audioStream);
			recordedChunksRef.current = [];

			// When data available, add to chunks
			mediaRecorderRef.current.ondataavailable = (e) => {
				if (e.data.size > 0) {
					recordedChunksRef.current.push(e.data);
				}
			};

			// When recording stops, create a blob and set media state
			mediaRecorderRef.current.onstop = () => {
				const audioBlob = new Blob(recordedChunksRef.current, {
					type: "audio/webm",
				});

				// Create object URL for audio preview
				const audioUrl = URL.createObjectURL(audioBlob);
				setCapturedAudio(audioUrl);

				// Stop all audio tracks
				if (audioRecorderRef.current) {
					audioRecorderRef.current.getTracks().forEach((track) => track.stop());
				}

				// Clean up audio context
				if (audioContextRef.current) {
					audioContextRef.current.close();
					audioContextRef.current = null;
				}
				if (animationFrameRef.current) {
					cancelAnimationFrame(animationFrameRef.current);
					animationFrameRef.current = null;
				}
				setAudioLevels([]);

				if (!showPreview) {
					const file = new File([audioBlob], `audio-${Date.now()}.webm`, {
						type: "audio/webm",
					});
					onCapture(file);
					handleClose();
				}
			};

			// Start recording
			mediaRecorderRef.current.start();
			setIsAudioRecording(true);
			setRecordingDuration(0);

			// Start visualization loop
			const bufferLength = analyserRef.current.frequencyBinCount;
			const dataArray = new Uint8Array(bufferLength);
			const updateVisualization = () => {
				if (
					!analyserRef.current ||
					!mediaRecorderRef.current ||
					mediaRecorderRef.current.state !== "recording"
				) {
					if (animationFrameRef.current) {
						cancelAnimationFrame(animationFrameRef.current);
						animationFrameRef.current = null;
					}
					return;
				}
				analyserRef.current.getByteFrequencyData(dataArray);
				// Get 20 bars for visualization (sample every nth value)
				const bars = 20;
				const step = Math.floor(bufferLength / bars);
				const levels = [];
				for (let i = 0; i < bars; i++) {
					const value = dataArray[i * step];
					levels.push(value);
				}
				setAudioLevels(levels);
				animationFrameRef.current = requestAnimationFrame(updateVisualization);
			};
			updateVisualization();
		} catch (error) {
			console.error("Error accessing microphone:", error);
			setError(
				"Could not access microphone. Please check your browser permissions."
			);
			setIsAudioRecording(false);
		}
	};

	const stopAudioRecording = () => {
		if (mediaRecorderRef.current && isAudioRecording) {
			mediaRecorderRef.current.stop();

			// Clean up audio context and animation
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = null;
			}
			if (audioContextRef.current) {
				audioContextRef.current.close();
				audioContextRef.current = null;
			}
			setAudioLevels([]);

			setIsAudioRecording(false);
		}
	};

	const handleRetake = async () => {
		// Clean up crop state
		if (imageToCrop) {
			URL.revokeObjectURL(imageToCrop);
			setImageToCrop(null);
		}
		setShowCrop(false);
		setCrop(null);
		setCompletedCrop(null);

		// Clean up blob URLs from previous captures to free memory
		if (capturedImage) {
			URL.revokeObjectURL(capturedImage);
		}
		if (capturedVideo) {
			URL.revokeObjectURL(capturedVideo);
		}
		if (capturedAudio) {
			URL.revokeObjectURL(capturedAudio);
		}

		// Stop audio recording if active
		if (isAudioRecording) {
			stopAudioRecording();
		}
		if (audioRecorderRef.current) {
			audioRecorderRef.current.getTracks().forEach((track) => track.stop());
			audioRecorderRef.current = null;
		}
		if (audioContextRef.current) {
			audioContextRef.current.close();
			audioContextRef.current = null;
		}
		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
			animationFrameRef.current = null;
		}

		// Clear captured media
		setCapturedImage(null);
		setCapturedVideo(null);
		setCapturedAudio(null);
		setAudioLevels([]);
		setRecordingDuration(0);
		setError(null);

		// Clear video element source before restarting
		if (videoRef.current) {
			videoRef.current.srcObject = null;
		}

		// Restart camera (startCamera will handle stopping any existing stream)
		await startCamera();
	};

	const handleFileSelect = (e, type) => {
		const file = e.target.files[0];
		if (!file) {return;}

		setError(null);

		// Validate file type
		const validTypes = {
			image: ["image/jpeg", "image/png", "image/gif", "image/webp"],
			video: ["video/mp4", "video/webm", "video/ogg", "video/quicktime"],
			audio: [
				"audio/mpeg",
				"audio/wav",
				"audio/ogg",
				"audio/webm",
				"audio/mp4",
			],
		};

		if (!validTypes[type]?.includes(file.type)) {
			setError(`Invalid file type. Please select a valid ${type} file.`);
			return;
		}

		// Check file size (10MB max)
		const maxSize = 10 * 1024 * 1024; // 10MB
		if (file.size > maxSize) {
			setError("File too large. Maximum size is 10MB.");
			return;
		}

		// Create preview URL
		const previewUrl = URL.createObjectURL(file);

		if (type === "image") {
			setCapturedImage(previewUrl);
		} else if (type === "video") {
			setCapturedVideo(previewUrl);
		} else if (type === "audio") {
			setCapturedAudio(previewUrl);
		}

		// If showPreview is false, immediately call onCapture
		if (!showPreview) {
			onCapture(file);
			handleClose();
		}
	};

	const handleClose = () => {
		stopCamera();
		onClose();
	};

	const formatDuration = (seconds) => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
	};

	// Ensure preview video plays when captured
	useEffect(() => {
		if (!capturedVideo || !previewVideoRef.current) {return;}

		const video = previewVideoRef.current;

		// Clear any existing srcObject to ensure clean state
		video.srcObject = null;

		// Ensure video is muted for autoplay (required by browsers)
		video.muted = true;

		// Wait for blob URL to be ready, then set src
		const initVideo = () => {
			if (!video.src || video.src !== capturedVideo) {
				video.src = capturedVideo;
				console.log("[Desktop] Video src set:", capturedVideo);
			}

			// Explicitly load the video (required for desktop browsers with blob URLs)
			if (video.readyState === 0) {
				video.load();
				console.log("[Desktop] Video load() called");
			}
		};

		// Longer delay to ensure blob URL is fully ready (helps with desktop buffering)
		const initTimeout = setTimeout(initVideo, 300);

		let hasStartedPlaying = false;

		// Function to attempt playing the video - only play when fully buffered
		const tryPlay = async () => {
			// Prevent multiple play attempts
			if (hasStartedPlaying) {
				return;
			}

			try {
				// Only play when we have enough data buffered (readyState >= 4 = HAVE_ENOUGH_DATA)
				// This prevents the "choking up" / buffering loop issue on desktop
				if (video.readyState >= 4 && video.paused) {
					video.currentTime = 0; // Reset to start
					video.muted = true;
					await video.play();
					hasStartedPlaying = true;
					console.log(
						"[Desktop] ✅ Video started playing successfully (fully buffered)"
					);
				} else if (video.readyState >= 4 && !video.paused) {
					// Video is fully buffered and already playing
					hasStartedPlaying = true;
				} else {
					console.log(
						"[Desktop] ⏳ Waiting for more buffering - readyState:",
						video.readyState,
						"(need >= 4 for smooth playback)"
					);
				}
			} catch (error) {
				console.error("[Desktop] ❌ Error playing video:", error);
				// Controls are visible, user can play manually
			}
		};

		// Handle when video can play through (fully buffered) - this is the key event for desktop
		const handleCanPlayThrough = () => {
			console.log(
				"[Desktop] 🎬 Can play through (fully buffered) - readyState:",
				video.readyState
			);
			video.muted = true;
			// Only try to play when fully buffered to prevent buffering loops
			if (!hasStartedPlaying) {
				tryPlay();
			}
		};

		// Handle when video can play (partially buffered) - just log, don't play yet
		const handleCanPlay = () => {
			console.log(
				"[Desktop] Can play (partially buffered) - readyState:",
				video.readyState
			);
			video.muted = true;
			// Don't play here - wait for canplaythrough for smoother playback
		};

		const handleLoadedMetadata = () => {
			console.log(
				"[Desktop] Metadata loaded - duration:",
				video.duration,
				"readyState:",
				video.readyState
			);
			video.muted = true;
		};

		const handleLoadedData = () => {
			console.log("[Desktop] 📦 Data loaded - readyState:", video.readyState);
			video.muted = true;
			// Don't try to play here - wait for more buffering
		};

		const handleProgress = () => {
			// Log buffering progress
			if (video.buffered.length > 0) {
				const bufferedEnd = video.buffered.end(video.buffered.length - 1);
				const bufferedPercent = (bufferedEnd / video.duration) * 100;
				console.log(
					"[Desktop] Buffering progress:",
					bufferedPercent.toFixed(1) + "%",
					"readyState:",
					video.readyState
				);

				// If we have enough buffered and haven't started playing, try to play
				if (
					bufferedPercent >= 50 &&
					video.readyState >= 4 &&
					!hasStartedPlaying
				) {
					tryPlay();
				}
			}
		};

		const handleWaiting = () => {
			console.log("[Desktop] ⏳ Waiting for data (buffering)...");
		};

		const handleStalled = () => {
			console.log("[Desktop] ⚠️ Video stalled (buffering issue)");
		};

		// Add event listeners - wait for canplaythrough before playing
		video.addEventListener("canplaythrough", handleCanPlayThrough, {
			once: true,
		});
		video.addEventListener("canplay", handleCanPlay, { once: false });
		video.addEventListener("loadedmetadata", handleLoadedMetadata, {
			once: false,
		});
		video.addEventListener("loadeddata", handleLoadedData, { once: false });
		video.addEventListener("progress", handleProgress, { once: false });
		video.addEventListener("waiting", handleWaiting, { once: false });
		video.addEventListener("stalled", handleStalled, { once: false });

		// Fallback: after a longer delay, check if video is ready and hasn't started
		const timeoutId = setTimeout(() => {
			console.log(
				"[Desktop] Final check - src:",
				video.src,
				"readyState:",
				video.readyState,
				"paused:",
				video.paused,
				"hasStartedPlaying:",
				hasStartedPlaying
			);

			// Only try to play if we have enough data and haven't started yet
			if (
				video.src &&
				video.readyState >= 4 &&
				video.paused &&
				!hasStartedPlaying
			) {
				console.log(
					"[Desktop] ✅ Final check: Video fully buffered, attempting play"
				);
				tryPlay();
			} else if (video.src && video.readyState === 0) {
				// Video has src but hasn't started loading - trigger load again
				console.log("[Desktop] Video src set but not loading, triggering load");
				video.load();
			}
		}, 1000); // Longer delay to ensure blob is fully ready and buffered

		return () => {
			clearTimeout(timeoutId);
			clearTimeout(initTimeout);
			hasStartedPlaying = false;
			video.removeEventListener("canplaythrough", handleCanPlayThrough);
			video.removeEventListener("canplay", handleCanPlay);
			video.removeEventListener("loadedmetadata", handleLoadedMetadata);
			video.removeEventListener("loadeddata", handleLoadedData);
			video.removeEventListener("progress", handleProgress);
			video.removeEventListener("waiting", handleWaiting);
			video.removeEventListener("stalled", handleStalled);
		};
	}, [capturedVideo]);

	return (
		<div className="fixed inset-0 z-[99999] flex flex-col bg-black">
			{/* Header */}
			<div className="absolute left-0 right-0 top-0 z-10 flex items-center justify-between bg-gradient-to-b from-black/80 to-transparent p-4">
				<Button
					onClick={handleClose}
					variant="ghost"
					size="icon"
					className="text-white hover:bg-white/20"
				>
					<X size={24} />
				</Button>

				{!capturedImage && !capturedVideo && hasPermission && (
					<Button
						onClick={switchCamera}
						variant="ghost"
						size="icon"
						className="text-white hover:bg-white/20"
					>
						<RotateCw size={24} />
					</Button>
				)}
			</div>

			{/* Hidden file inputs */}
			<input
				ref={fileInputRef}
				type="file"
				accept="image/jpeg,image/png,image/gif,image/webp"
				onChange={(e) => handleFileSelect(e, "image")}
				className="hidden"
			/>
			<input
				ref={videoInputRef}
				type="file"
				accept="video/mp4,video/webm,video/ogg,video/quicktime"
				onChange={(e) => handleFileSelect(e, "video")}
				className="hidden"
			/>
			<input
				ref={audioInputRef}
				type="file"
				accept="audio/mpeg,audio/wav,audio/ogg,audio/webm,audio/mp4"
				onChange={(e) => handleFileSelect(e, "audio")}
				className="hidden"
			/>

			{/* Preview controls - moved to top to avoid overlapping video player controls */}
			{(capturedImage || capturedVideo || capturedAudio) && (
				<div className="absolute left-0 right-0 top-16 z-10 flex items-center justify-center gap-4 p-4">
					{/* Undo button - only show for images (to go back to crop screen) */}
					{capturedImage && imageToCrop && (
						<Button
							onClick={handleUndo}
							size="lg"
							variant="outline"
							className="border-white/30 bg-white/10 px-8 text-white backdrop-blur-sm hover:bg-white/20"
							title="Undo crop and return to crop screen"
						>
							<Undo2 size={20} className="mr-2" />
							Undo
						</Button>
					)}
					<Button
						onClick={handleRetake}
						size="lg"
						variant="outline"
						className="border-white/30 bg-white/10 px-8 text-white backdrop-blur-sm hover:bg-white/20"
					>
						Retake
					</Button>
					<Button
						onClick={handleConfirm}
						size="lg"
						className="bg-green-500 px-8 text-white hover:bg-green-600"
					>
						<CheckCircle size={20} className="mr-2" />
						Use{" "}
						{mode === "photo"
							? "Photo"
							: mode === "video"
								? "Video"
								: mode === "audio"
									? "Audio"
									: "Media"}
					</Button>
				</div>
			)}

			{/* Camera View or Preview */}
			<div className="relative flex flex-1 items-center justify-center bg-black">
				{error ? (
					<div className="p-8 text-center text-white">
						<CameraIcon size={64} className="mx-auto mb-4 opacity-50" />
						<p className="mb-2 text-lg">{error}</p>
						<p className="text-sm text-gray-400">
							Please allow camera access in your browser settings
						</p>
					</div>
				) : showCrop && imageToCrop ? (
					<div className="relative flex h-full w-full items-center justify-center bg-black p-4">
						<ReactCrop
							crop={crop}
							onChange={onCropChange}
							onComplete={onCropComplete}
							aspect={1}
							minWidth={100}
							minHeight={100}
							keepSelection={true}
							disabled={false}
						>
							<img
								ref={imgRef}
								src={imageToCrop}
								alt="Crop"
								style={{
									maxWidth: "100%",
									maxHeight: "100%",
									objectFit: "contain",
									display: "block",
								}}
								onLoad={onImageLoaded}
							/>
						</ReactCrop>
					</div>
				) : capturedImage ? (
					<img
						src={capturedImage}
						alt="Captured"
						className="max-h-full max-w-full object-contain"
					/>
				) : capturedVideo ? (
					<div className="relative flex h-full w-full items-center justify-center bg-black">
						<video
							key={capturedVideo} // Force re-render when video changes
							ref={previewVideoRef}
							src={capturedVideo}
							controls
							autoPlay
							muted
							playsInline
							loop
							preload="auto"
							className="max-h-full max-w-full rounded-lg border-2 border-white/20 bg-black object-contain shadow-2xl"
							style={{
								width: "100%",
								height: "100%",
								outline: "none",
								maxWidth: "480px",
								aspectRatio: "9 / 16",
							}}
							onLoadedMetadata={(e) => {
								const video = e.target;
								console.log(
									"[Video] Metadata loaded - duration:",
									video.duration,
									"readyState:",
									video.readyState,
									"src:",
									video.src
								);
								// Ensure muted for autoplay
								video.muted = true;
							}}
							onLoadedData={(e) => {
								const video = e.target;
								console.log(
									"[Video] Data loaded - readyState:",
									video.readyState
								);
								// Ensure muted for autoplay
								video.muted = true;
							}}
							onCanPlay={(e) => {
								const video = e.target;
								console.log(
									"[Video] Can play (partial buffering) - paused:",
									video.paused,
									"readyState:",
									video.readyState
								);
								// Ensure muted - wait for canplaythrough for full buffering before playing
								video.muted = true;
								// Don't play here - wait for full buffering to prevent buffering loops
							}}
							onCanPlayThrough={(e) => {
								const video = e.target;
								console.log(
									"[Video] Can play through (fully buffered) - readyState:",
									video.readyState
								);
								// Video is fully buffered - ensure muted for autoplay
								video.muted = true;
								// Let useEffect handle playback to avoid conflicts
							}}
							onPlay={() => {
								console.log("[Video] Started playing successfully!");
							}}
							onPlaying={() => {
								console.log("[Video] Video is now playing");
							}}
							onError={(e) => {
								const video = e.target;
								console.error(
									"[Video] Error - code:",
									video.error?.code,
									"message:",
									video.error?.message,
									"src:",
									video.src
								);
								setError(
									"Error loading video preview. Please try recording again."
								);
							}}
							onWaiting={() => {
								console.log("[Video] Waiting for data...");
							}}
						>
							Your browser does not support the video tag.
						</video>
						{/* Instruction overlay */}
						<div className="pointer-events-none absolute left-1/2 top-4 z-10 -translate-x-1/2 transform rounded-full border border-white/20 bg-black/80 px-4 py-2 text-sm text-white backdrop-blur-sm">
							Tap video to pause/play
						</div>
						{/* Visual indicator that this is a video player */}
						<div className="pointer-events-none absolute bottom-20 left-1/2 -translate-x-1/2 transform rounded-md border border-white/20 bg-black/60 px-3 py-1 text-xs text-white backdrop-blur-sm">
							Video Preview (Looping)
						</div>
					</div>
				) : capturedAudio ? (
					<div className="relative flex h-full w-full flex-col items-center justify-center bg-black p-8">
						<Mic size={64} className="mb-4 text-white opacity-50" />
						<audio src={capturedAudio} controls className="w-full max-w-md" />
						<p className="mt-4 text-sm text-white opacity-75">Audio Preview</p>
					</div>
				) : (
					<>
						<div className="flex h-full w-full items-center justify-center bg-black">
							<video
								ref={videoRef}
								autoPlay
								playsInline
								muted
								className="object-cover"
								style={{
									width: "100%",
									height: "100%",
									maxWidth: "480px",
									aspectRatio: "9 / 16",
									transform: facingMode === "user" ? "scaleX(-1)" : "scaleX(1)",
								}}
							/>
						</div>

						{/* Recording indicator */}
						{isRecording && (
							<div className="absolute left-1/2 top-20 flex -translate-x-1/2 transform items-center gap-2 rounded-full bg-red-500 px-4 py-2 text-white shadow-lg">
								<span className="h-3 w-3 animate-pulse rounded-full bg-white"></span>
								<span className="font-mono text-lg font-bold">
									{formatDuration(recordingDuration)}
								</span>
							</div>
						)}
					</>
				)}
			</div>

			{/* Hidden canvas for photo capture */}
			<canvas ref={canvasRef} className="hidden" />

			{/* Controls */}
			<div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/80 to-transparent">
				{showCrop && imageToCrop ? (
					// Crop controls
					<div className="flex flex-col items-center gap-4 p-8">
						<div className="flex gap-4">
							<Button
								onClick={() => {
									setShowCrop(false);
									setCrop(null);
									if (imageToCrop) {
										URL.revokeObjectURL(imageToCrop);
										setImageToCrop(null);
									}
								}}
								variant="outline"
								className="border-white/30 bg-white/10 text-white hover:bg-white/20"
							>
								<X size={18} className="mr-2" />
								Cancel
							</Button>
							<Button
								onClick={applyCrop}
								disabled={!crop}
								className="bg-white text-black hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
							>
								Apply Crop
							</Button>
						</div>
						<p className="text-center text-xs text-white">
							Drag the crop box to move it, drag corners to resize
						</p>
					</div>
				) : !capturedImage && !capturedVideo && !capturedAudio ? (
					// Capture controls with mode selector
					<div className="flex flex-col items-center gap-4 pb-0">
						{/* Waveform visualization when recording audio */}
						{isAudioRecording && (
							<div className="pt-4">
								<Waveform
									audioLevels={audioLevels}
									isRecording={isAudioRecording}
									style={{ height: "64px" }}
								/>
							</div>
						)}

						{/* Capture Button */}
						<div className="flex items-center justify-center pb-4">
							{mode === "photo" ? (
								<Button
									onClick={capturePhoto}
									disabled={!hasPermission || isAudioRecording}
									size="icon"
									className="h-20 w-20 rounded-full border-4 border-white bg-white text-black shadow-xl hover:bg-gray-200"
								>
									<Circle size={40} className="fill-current" />
								</Button>
							) : mode === "audio" ? (
								<Button
									onClick={
										isAudioRecording ? stopAudioRecording : startAudioRecording
									}
									disabled={!hasPermission}
									size="icon"
									className={`h-20 w-20 rounded-full border-4 border-white shadow-xl transition-all ${
										isAudioRecording
											? "bg-red-500 hover:bg-red-600"
											: "bg-white hover:bg-gray-200"
									}`}
								>
									{isAudioRecording ? (
										<div className="h-8 w-8 rounded-sm bg-white"></div>
									) : (
										<Mic size={40} className="text-black" />
									)}
								</Button>
							) : (
								<Button
									onClick={isRecording ? stopRecording : startRecording}
									disabled={!hasPermission || isAudioRecording}
									size="icon"
									className={`h-20 w-20 rounded-full border-4 border-white shadow-xl transition-all ${
										isRecording
											? "bg-red-500 hover:bg-red-600"
											: "bg-white hover:bg-gray-200"
									}`}
								>
									{isRecording ? (
										<div className="h-8 w-8 rounded-sm bg-white"></div>
									) : (
										<Circle size={40} className="fill-red-500 text-red-500" />
									)}
								</Button>
							)}
						</div>

						{/* Recording duration */}
						{(isAudioRecording || isRecording) && (
							<p className="pb-2 text-sm font-medium text-white">
								{isAudioRecording
									? "Recording audio..."
									: isRecording
										? "Recording..."
										: ""}
								{recordingDuration > 0 &&
									` ${formatDuration(recordingDuration)}`}
							</p>
						)}

						{/* Bottom bar with mode switcher and upload button */}
						<div className="flex w-full items-center justify-between gap-4 rounded-t-2xl border-t border-white/20 bg-black/60 px-4 py-3 backdrop-blur-sm">
							{/* Upload button */}
							<Button
								onClick={() => {
									// Trigger appropriate input based on mode
									if (mode === "photo") {
										fileInputRef.current?.click();
									} else if (mode === "video") {
										videoInputRef.current?.click();
									} else if (mode === "audio") {
										audioInputRef.current?.click();
									}
								}}
								size="icon"
								variant="ghost"
								className="h-10 w-10 rounded-full bg-white/10 text-white backdrop-blur-sm hover:bg-white/20"
								title="Upload file"
							>
								<Upload size={20} />
							</Button>

							{/* Mode Toggle Buttons */}
							<div className="flex gap-2 rounded-full bg-black/40 p-1 backdrop-blur-sm">
								<Button
									onClick={() => setMode("photo")}
									disabled={isRecording || isAudioRecording}
									size="icon"
									className={`h-10 w-10 rounded-full transition-all ${
										mode === "photo"
											? "bg-white text-black hover:bg-gray-200"
											: "bg-transparent text-white hover:bg-white/10"
									}`}
									title="Photo"
								>
									<CameraIcon size={20} />
								</Button>
								<Button
									onClick={() => setMode("video")}
									disabled={isRecording || isAudioRecording}
									size="icon"
									className={`h-10 w-10 rounded-full transition-all ${
										mode === "video"
											? "bg-white text-black hover:bg-gray-200"
											: "bg-transparent text-white hover:bg-white/10"
									}`}
									title="Video"
								>
									<Video size={20} />
								</Button>
								<Button
									onClick={() => setMode("audio")}
									disabled={isRecording || isAudioRecording}
									size="icon"
									className={`h-10 w-10 rounded-full transition-all ${
										mode === "audio"
											? "bg-white text-black hover:bg-gray-200"
											: "bg-transparent text-white hover:bg-white/10"
									}`}
									title="Audio"
								>
									<Mic size={20} />
								</Button>
							</div>

							{/* Spacer to balance layout */}
							<div className="h-10 w-10" />
						</div>
					</div>
				) : null}
			</div>
		</div>
	);
};

export default Camera;
